/*
 * VH-103 — Compact, paper-shaped render of a single recipe.
 *
 * Rendered off-screen on the recipe detail page (positioned far left so
 * html2canvas can rasterize it without the user seeing it). When the body
 * carries the `printing-recipe` class, the print stylesheet promotes this
 * view to full width and hides the rest of the SPA chrome.
 *
 * Content is intentionally minimal (cover image, title, servings line,
 * ingredients, steps) — no macros, no video, no equipment compat. See the
 * VH-103 spec for the rationale.
 */

import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import RecipeTips from './RecipeTips'
import StepIngredientLine from './StepIngredientLine'
import { buildStepIngredients } from '../../utils/recipeStepIngredients'

function joinNonEmpty(parts, sep) {
  return parts.filter((p) => p !== null && p !== undefined && p !== '').join(sep)
}

const RecipePrintableView = forwardRef(function RecipePrintableView(
  {
    recipe,
    version,
    displayedTitle,
    displayedTips,
    translatedSteps,
    translatedIngredients,
  },
  ref
) {
  const { t } = useTranslation()

  if (!recipe || !version) return null

  const cover = recipe.cover_image_url || version.photo_url || null
  const title = displayedTitle || version.title
  // VH-106: explicit prop wins; otherwise fall back to source.
  const tips =
    typeof displayedTips !== 'undefined' ? displayedTips : version.tips

  const servingsLine = joinNonEmpty(
    [
      version.servings
        ? t('recipes.print.servings', { count: version.servings })
        : null,
      version.prep_minutes
        ? t('recipes.print.prep', { count: version.prep_minutes })
        : null,
      version.cook_minutes
        ? t('recipes.print.cook', { count: version.cook_minutes })
        : null,
    ],
    ' · '
  )

  const ingredients = (version.ingredients || [])
    .slice()
    .sort((a, b) => a.display_order - b.display_order)

  const steps = (version.steps || [])
    .slice()
    .sort((a, b) => a.step_order - b.step_order)

  // VH-209: the amounts a step uses, so the cook is not sent back up the page.
  // Computed from the version being rendered, which is why a scaled recipe
  // prints its scaled numbers without anything being stored.
  const stepIngredients = buildStepIngredients(steps, ingredients, {
    translatedSteps,
    translatedIngredients,
  })

  return (
    <div
      ref={ref}
      className="recipe-printable bg-surface-container text-black mx-auto p-8"
      style={{ width: '210mm', maxWidth: '210mm', minHeight: '297mm' }}
      data-testid="recipe-printable-view"
    >
      {cover ? (
        <img
          src={cover}
          alt={title}
          className="recipe-printable__cover w-full mb-5 rounded"
          style={{ maxHeight: '110mm', objectFit: 'cover' }}
          crossOrigin="anonymous"
          data-testid="recipe-printable-cover"
        />
      ) : null}

      <h1
        className="recipe-printable__title text-3xl font-bold mb-2"
        data-testid="recipe-printable-title"
      >
        {title}
      </h1>

      {servingsLine && (
        <p
          className="recipe-printable__meta text-sm text-gray-700 mb-6"
          data-testid="recipe-printable-meta"
        >
          {servingsLine}
        </p>
      )}

      <section
        className="recipe-printable__ingredients mb-6"
        data-testid="recipe-printable-ingredients"
      >
        <h2 className="text-xl font-semibold mb-3">
          {t('recipes.detail.ingredientsTitle')}
        </h2>
        {ingredients.length ? (
          <ul className="list-disc pl-6 space-y-1">
            {ingredients.map((ing) => {
              const amountUnit = joinNonEmpty([ing.amount, ing.unit], ' ')
              const name =
                translatedIngredients?.[ing.id] || ing.food_name_snapshot
              const main = joinNonEmpty([amountUnit, name], ' ')
              return (
                <li
                  key={ing.id}
                  className="text-base"
                  data-testid={`recipe-printable-ingredient-${ing.id}`}
                >
                  {main}
                  {ing.notes ? (
                    <span className="text-sm text-gray-600">
                      {' '}({ing.notes})
                    </span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">
            {t('recipes.detail.noIngredients')}
          </p>
        )}
      </section>

      <section
        className="recipe-printable__steps"
        data-testid="recipe-printable-steps"
      >
        <h2 className="text-xl font-semibold mb-3">
          {t('recipes.detail.stepsTitle')}
        </h2>
        {steps.length ? (
          <ol className="list-decimal pl-6 space-y-2">
            {steps.map((step) => {
              const text = translatedSteps?.[step.id] || step.text
              const tail = joinNonEmpty(
                [
                  step.timer_minutes
                    ? t('recipes.minutesShort', {
                        count: step.timer_minutes,
                      })
                    : null,
                  step.min_oven_temp_c
                    ? `${step.min_oven_temp_c} °C`
                    : null,
                ],
                ' · '
              )
              return (
                <li
                  key={step.id}
                  className="text-base"
                  data-testid={`recipe-printable-step-${step.id}`}
                >
                  {text}
                  {tail && (
                    <span className="text-sm text-gray-600"> · {tail}</span>
                  )}
                  <StepIngredientLine items={stepIngredients[step.id]} />
                </li>
              )
            })}
          </ol>
        ) : (
          <p className="text-sm text-gray-500">
            {t('recipes.detail.noSteps')}
          </p>
        )}
      </section>

      {/* VH-106: Tips & Recommendations — rendered below steps, omitted
          entirely when empty. */}
      {(tips || '').trim() ? (
        <section
          className="recipe-printable__tips mt-6"
          data-testid="recipe-printable-tips"
        >
          <h2 className="text-xl font-semibold mb-3">
            {t('recipes.detail.tipsTitle')}
          </h2>
          <RecipeTips tips={tips} />
        </section>
      ) : null}
    </div>
  )
})

export default RecipePrintableView
